import React, { useCallback, useContext, useMemo, useState } from 'react';
import { RCMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { ArchiveRow } from '@shared/components/list';
import { TextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { componentTemplateOptions } from '@shared/scenes/workflows/config';
import {
  useWorkflowDispatch,
  useWorkflowTemplateState,
} from '@shared/scenes/workflows/context/context';
import { OptionsData } from '@shared/scenes/workflows/template/options/OptionsData';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { Divider } from './divider';

interface Props {
  component: MBAPI.WorkflowTemplate_components_DateRangeComponent;
}

export function DateRangeOptions({ component }: Props) {
  const colors = useContext(ColorThemeContext);
  const [label, setLabel] = useState(component.label || '');
  const [placeholder, setPlaceholder] = useState(component.placeholder || '');
  const dispatch = useWorkflowDispatch();
  const { workflowTemplate } = useWorkflowTemplateState();

  const optionsData = useMemo<OptionsData>(
    () => componentTemplateOptions.optionsDataForComponent(component),
    [component],
  );

  const updateLabel = useCallback(
    (newLabel: string) => {
      setLabel(newLabel);

      const defaultLabel = i18n('card.blocks.dateRangeInput.defaultLabel');
      const startSuffix = i18n('card.blocks.dateRangeInput.startSuffix');
      const endSuffix = i18n('card.blocks.dateRangeInput.endSuffix');
      const effectiveLabel = newLabel || defaultLabel;

      // Update parent component
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          label: newLabel,
        },
      });

      // Update child start date component
      const startDateComponent = workflowTemplate.components.find(
        c => c.id === component.startDateComponentId,
      );
      if (startDateComponent) {
        dispatch({
          type: 'updateComponent',
          args: {
            ...startDateComponent,
            label: `${effectiveLabel}: ${startSuffix}`,
          },
        });
      }

      // Update child end date component
      const endDateComponent = workflowTemplate.components.find(
        c => c.id === component.endDateComponentId,
      );
      if (endDateComponent) {
        dispatch({
          type: 'updateComponent',
          args: {
            ...endDateComponent,
            label: `${effectiveLabel}: ${endSuffix}`,
          },
        });
      }
    },
    [dispatch, component, workflowTemplate],
  );

  const updatePlaceholder = useCallback(
    (newPlaceholder: string) => {
      setPlaceholder(newPlaceholder);

      const defaultLabel = i18n('card.blocks.dateRangeInput.defaultLabel');
      const startSuffix = i18n('card.blocks.dateRangeInput.startSuffix');
      const endSuffix = i18n('card.blocks.dateRangeInput.endSuffix');
      const effectivePlaceholder = newPlaceholder || defaultLabel;

      // Update parent component
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          placeholder: newPlaceholder,
        },
      });

      // Update child start date component
      const startDateComponent = workflowTemplate.components.find(
        c => c.id === component.startDateComponentId,
      );
      if (startDateComponent && 'placeholder' in startDateComponent) {
        dispatch({
          type: 'updateComponent',
          args: {
            ...startDateComponent,
            placeholder: `${effectivePlaceholder}: ${startSuffix}`,
          },
        });
      }

      // Update child end date component
      const endDateComponent = workflowTemplate.components.find(
        c => c.id === component.endDateComponentId,
      );
      if (endDateComponent && 'placeholder' in endDateComponent) {
        dispatch({
          type: 'updateComponent',
          args: {
            ...endDateComponent,
            placeholder: `${effectivePlaceholder}: ${endSuffix}`,
          },
        });
      }
    },
    [dispatch, component, workflowTemplate],
  );

  const onArchive = useCallback(() => {
    // Archive the date range component
    dispatch({
      type: 'archiveComponent',
      args: component.id,
    });

    // Archive the managed start date component
    if (component.startDateComponentId) {
      dispatch({
        type: 'archiveComponent',
        args: component.startDateComponentId,
      });
    }

    // Archive the managed end date component
    if (component.endDateComponentId) {
      dispatch({
        type: 'archiveComponent',
        args: component.endDateComponentId,
      });
    }
  }, [dispatch, component.id, component.startDateComponentId, component.endDateComponentId]);

  return (
    <View>
      <RCMenu
        mode="vertical"
        triggerSubMenuAction="click"
        style={{
          backgroundColor: 'white',
          borderRadius: grid(1),
          boxShadow: '0 0 10px #d9d9d9',
          maxHeight: '100vh',
          overflow: 'auto',
        }}
        expandIcon={null}
      >
        <View paddingHorizontal={3}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.fieldType')}
          </MBTextOverlineBold>
        </View>
        <View paddingHorizontal={3} paddingVertical={2} row centerVertically>
          <MBIconV2 color={colors.foregroundActive} name={optionsData.icon} size={grid(3)} />
          <View paddingLeft={1} />
          <MBTextBody1 color={colors.foregroundActive}>{optionsData.title}</MBTextBody1>
        </View>
        <View paddingHorizontal={2}>
          <Divider />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.label')}
          </MBTextOverlineBold>
          <View padding={1} />
          <TextInput value={label} onChangeText={updateLabel} />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.placeholder')}
          </MBTextOverlineBold>
          <View padding={1} />
          <TextInput value={placeholder} onChangeText={updatePlaceholder} />
        </View>
        <View paddingHorizontal={2} paddingTop={1}>
          <Divider />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.options')}
          </MBTextOverlineBold>
        </View>
        <ArchiveRow disabled={!!component.locked} onArchive={onArchive} />
      </RCMenu>
    </View>
  );
}
